import { useEffect, useState } from 'react';
import { Box, ButtonGroup, IconButton, Tab, Tabs } from '@mui/material';
import { APIProvider, ControlPosition, Map, MapControl, Marker, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import TravelInfo from './state/TravelInfo';
import WeatherCard from './state/WeatherCard';
import VehicleDataDisplay from './state/VehicleDataDisplay';

import CommuteIcon from '@mui/icons-material/Commute';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import WarningIconAmber from '@mui/icons-material/WarningAmber';
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import AlertList from './state/AlertList';
import { AddLocation, Lock, NoEncryption, Tune } from '@mui/icons-material';
import { gql, useQuery } from '@apollo/client';
import Settings from './Settings';
import { sendTelemetryToServer } from './SendTelemetry';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GMAP_API_KEY

const GET_ROUTE = gql`
query GetRoute($routeId: String) {
observation(criteria:{sensor:"com.navscout.sensor.derived.DeriveRouteDetails", subjectId: $routeId}) {
    observations {
        title
        content
    }
    countAvailable
    countReturned
}
}`

const defaultLocation = {
    latitude: 37.7749, // Example: San Francisco
    longitude: -122.4194,
    locality: 'San Francisco',
    adminDistrict: 'California',
    country: 'United States'
};

const Cards = ({ activeState, account, client }) => {
    // State to keep track of the currently selected tab.
    // We'll use a number (index) for the value, starting from 0.
    const [value, setValue] = useState(0);

    const alertCount = activeState?.upcomingEvents?.filter(e => e.severity === 'HAZARD' || e.severity === 'CIRITICAL')?.length || 0;

    // Handler for when the tab selection changes
    const handleChange = (event, newValue) => {
        setValue(newValue);
    };
    return (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Tab controls are a fixed-height element at the top */}
            <Box sx={{ borderTop: 1, borderColor: 'divider', width: '100%', flexShrink: 0 }}>
                <Tabs
                    value={value}
                    onChange={handleChange}
                    aria-label="dashboard tabs"
                    textColor="primary"
                    indicatorColor="primary"
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    <Tab label="Travel" icon={<CommuteIcon />} iconPosition="bottom" />
                    <Tab label="Weather" icon={<WbSunnyIcon />} iconPosition="bottom" />
                    <Tab label="Alerts" icon={<WarningIconAmber sx={{ color: alertCount > 0 ? 'red' : 'default' }} />} iconPosition="bottom" />
                    <Tab label="Vehicle" icon={<DriveEtaIcon />} iconPosition="bottom" />
                </Tabs>
            </Box>

            {/* The content area takes up the remaining space and is scrollable */}
            <Box
                sx={{
                    padding: 1,
                    overflowY: 'auto', // This is the key change
                    flexGrow: 1, // This allows the content area to fill the remaining vertical space
                }}
            >
                {value === 0 && (
                    <TravelInfo activeState={activeState} account={account} client={client} />
                )}
                {value === 1 && (
                    <WeatherCard location={activeState?.location} weatherData={activeState?.currentWeather} />
                )}
                {value === 2 && (
                    <AlertList events={activeState?.upcomingEvents} />
                )}
                {value === 3 && (
                    <VehicleDataDisplay activeState={activeState} />
                )}
            </Box>
        </Box>
    );
}
const APP_BAR_HEIGHT = 0

const LoadRoute = ({ account, client, route, onLoad }) => {
    useQuery(GET_ROUTE, {
        client,
        skip: !route?.id,
        fetchPolicy: 'network-only',
        variables: { routeId: route?.id },
        onCompleted: (data) => {
            const json = data?.observation?.observations[0]?.content
            onLoad(JSON.parse(json));
        },
        onError: (error) => {
            console.error('Error loading route:', error);
        },
    });
}


function Route({ route }) {
    const map = useMap();
    const geometryLibrary = useMapsLibrary('geometry');
    const mapsLibrary = useMapsLibrary('maps');
    const [polyline, setPolyline] = useState(null);

    // Polyline options (can be customized as needed)
    const polylineOptions = {
        strokeColor: '#000000',
        strokeOpacity: 1.0,
        strokeWeight: 3,
    };

    // Create polyline once mapsLibrary is available
    useEffect(() => {
        if (!mapsLibrary) return;

        const newPolyline = new mapsLibrary.Polyline(polylineOptions);
        setPolyline(newPolyline);

        // Cleanup: Remove polyline from map when component unmounts
        return () => newPolyline.setMap(null);
    }, [mapsLibrary]);

    // Update polyline path when route data changes
    useEffect(() => {
        if (!geometryLibrary || !polyline || !route?.labels || !route?.data) {
            // Only clear the path if polyline is already on the map
            if (polyline?.getMap()) {
                polyline.setPath([]);
            }
            return;
        }

        const latIndex = route.labels.indexOf('Latitude');
        const lngIndex = route.labels.indexOf('Longitude');

        // Validate indices
        if (latIndex === -1 || lngIndex === -1) {
            console.warn('Latitude or Longitude not found in route labels');
            polyline.setPath([]);
            return;
        }

        // Create path from route data
        const path = route.data[0].map((_, i) => ({
            lat: route.data[latIndex][i],
            lng: route.data[lngIndex][i],
        }));

        polyline.setPath(path);

        // Add polyline to map if not already added
        if (!polyline.getMap()) {
            polyline.setMap(map);
        }
    }, [geometryLibrary, polyline, route, map]);

    return null;
}

function LocationMarker({ location }) {
    if (!location || !location.longitude || !location.latitude) {
        return null
    }
    return (<Marker position={{ lng: location.longitude, lat: location.latitude }} />)
}

export default function Dashboard({ account, activeState, client }) {
    const { latitude, longitude, locality, adminDistrict, country } = (activeState?.location || defaultLocation);
    const [locked, setLocked] = useState(false);
    const [route, setRoute] = useState(null)
    const [showPowerMenu, setShowPowerMenu] = useState(null)
    const [manualTelemetry, setManualTelemetry] = useState(false)

    useEffect(() => {
        if (route && !activeState.travel) {
            console.log("Unset route")
            setRoute([])
        }
    }, [activeState])

    const center = { lat: latitude, lng: longitude };
    if (!activeState) {
        return null;
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: `calc(100vh - ${APP_BAR_HEIGHT}px)` }}> {/* Set up flex container and full viewport height */}
            <LoadRoute account={account} client={client} route={activeState?.travel} onLoad={loaded => setRoute(loaded)} />
            <Box sx={{ flexGrow: 1 }}> {/* Map takes up remaining space */}
                <APIProvider apiKey={GOOGLE_MAPS_API_KEY} onLoad={() => console.log('Maps API has loaded.')}>
                    <Map
                        style={{ width: '100%', height: '100%' }} // Map takes 100% of its parent's height
                        center={locked ? center : null}
                        defaultCenter={center}
                        defaultZoom={13}
                        disableDefaultUI={true}
                        onClick={e => {
                            if (manualTelemetry && e?.detail?.latLng) {
                                const latLng = e?.detail?.latLng
                                console.log(latLng)
                                sendTelemetryToServer(client, latLng.lng, latLng.lat, new Date())
                            }
                        }}
                        gestureHandling={manualTelemetry ? 'none' : null}
                    >
                        <MapControl position={ControlPosition.LEFT_CENTER}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                <Box sx={{ backgroundColor: 'white' }}>
                                    <IconButton onClick={() => setLocked(!locked)}>{locked ? <Lock /> : <NoEncryption />}</IconButton>
                                </Box>
                                {Settings.isPowerMode() && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', backgroundColor: 'white' }}>
                                        <IconButton onClick={() => setShowPowerMenu(!showPowerMenu)}>
                                            <Tune />
                                        </IconButton>

                                        {showPowerMenu && (
                                            <ButtonGroup
                                                variant="text"
                                                size="small"
                                                sx={{ ml: 1 }}
                                            >
                                                <IconButton><AddLocation color={manualTelemetry ? 'primary' : 'inherit'} onClick={() => setManualTelemetry(!manualTelemetry)}/></IconButton>
                                            </ButtonGroup>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </MapControl>
                        <Marker position={center} />
                        <LocationMarker location={activeState?.travel?.destination} />
                        <Route route={route} />
                    </Map>
                </APIProvider>
            </Box>
            <Box sx={{ height: '350px', flexShrink: 0 }}> {/* Fixed height for the bottom section */}
                <Cards account={account} activeState={activeState} client={client} />
            </Box>
        </Box>
    );
}
